var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { customElement, property } from 'lit/decorators.js';
import { html, nothing } from 'lit';
import { BwElement } from './base/bw-element.js';
import './bw-tag.js';
import './bw-button.js';
function formatMmSs(seconds) {
    const s = Math.max(0, Math.floor(seconds || 0));
    const mm = Math.floor(s / 60);
    const ss = s % 60;
    return `${mm}:${String(ss).padStart(2, '0')}`;
}
const SH_CHROMA = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const SH_FLAT = { Db: 'C#', Eb: 'D#', Gb: 'F#', Ab: 'G#', Bb: 'A#' };
function shTransposeKey(key, semis) {
    if (!key)
        return key ?? '';
    const m = key.trim().match(/^([A-G][#b]?)(.*)$/);
    if (!m)
        return key;
    const root = SH_FLAT[m[1]] ?? m[1];
    const i = SH_CHROMA.indexOf(root);
    if (i === -1)
        return key;
    return SH_CHROMA[(((i + semis) % 12) + 12) % 12] + m[2];
}
function parseMmSs(text) {
    const s = String(text || '').trim();
    if (!s)
        return null;
    let sec;
    if (s.includes(':')) {
        const parts = s.split(':').map(x => parseInt(x, 10));
        if (parts.length !== 2)
            return null;
        const [mm, ss] = parts;
        if (Number.isNaN(mm) || Number.isNaN(ss) || mm < 0 || ss < 0)
            return null;
        sec = mm * 60 + ss;
    }
    else {
        sec = parseInt(s, 10);
        if (Number.isNaN(sec) || sec < 0)
            return null;
    }
    return Math.min(3600, sec);
}
let BwSongHeader = class BwSongHeader extends BwElement {
    constructor() {
        super(...arguments);
        this.song = null;
        this.audioDuration = 0;
        this.compact = false;
        this.editableDuration = false;
        this.editableNotes = false;
        this.noSongLabel = 'No song';
        this.notesTooltip = '';
        this._onNotesDblClick = () => {
            if (!this.editableNotes)
                return;
            this.dispatchEvent(new CustomEvent('bw-song-header-notes-click', {
                bubbles: true, composed: true,
            }));
        };
        this._onKeyClick = () => {
            this.dispatchEvent(new CustomEvent('bw-song-header-key-click', { bubbles: true, composed: true }));
        };
        this._onTempoClick = () => {
            this.dispatchEvent(new CustomEvent('bw-song-header-tempo-click', { bubbles: true, composed: true }));
        };
        this._onSigClick = () => {
            this.dispatchEvent(new CustomEvent('bw-song-header-sig-click', { bubbles: true, composed: true }));
        };
        this._onDurationDblClick = (e) => {
            if (!this.editableDuration)
                return;
            const chip = e.currentTarget;
            const currentSec = Number(chip.dataset.sec) || 0;
            this._swapToInput(chip, currentSec);
        };
    }
    // Множитель темпа — ОДНО место на весь компонент. Им пользуется и `render`
    // (ярлык длительности = dur / rate), и `commit` внутри `_swapToInput`
    // (перерисовка ярлыка после правки). Пока копий было две, они разошлись:
    // приёмка 27.08 — уход фокусом из редактора возвращал в чип СЫРОЕ 5:00 вместо
    // звучащих 4:10, потому что императивная запись в DOM осталась на исходном
    // числе, когда ярлык стал производным.
    _rate() {
        const r = this.song?.playbackTempo;
        return (typeof r === 'number' && r > 0) ? r : 1;
    }
    render() {
        const song = this.song;
        const dur = (song?.duration && song.duration > 0) ? song.duration : this.audioDuration;
        return html `
      <div class="bw-sh-left">
        <div class="bw-sh-title">${song?.name ?? this.noSongLabel}</div>
        <div class="bw-sh-meta">
          ${song?.key ? (() => {
            const semis = song.playbackTranspose ?? 0;
            const active = semis !== 0;
            return html `<bw-button kind="default" size="md" data-chip="key"
              tone="default"
              ?selected=${active}
              ?data-active=${active}
              @click=${this._onKeyClick}
            >${shTransposeKey(song.key, semis)}${active ? html `<span class="bw-sh-badge">${semis > 0 ? '+' : ''}${semis}</span>` : nothing}</bw-button>`;
        })() : nothing}
          ${song?.bpm ? (() => {
            const rate = this._rate();
            const active = rate !== 1;
            const effBpm = Math.round(song.bpm * rate);
            return html `<bw-button kind="default" size="md" data-chip="bpm"
              tone="default"
              ?selected=${active}
              ?data-active=${active}
              @click=${this._onTempoClick}
            >BPM ${effBpm}</bw-button>`;
        })() : nothing}
          ${song?.timeSignature
            ? html `<bw-button kind="default" size="md" data-chip="sig"
                @click=${this._onSigClick}
              >${song.timeSignature}</bw-button>`
            : nothing}
          ${song ? (() => {
            // B-260818 — длительность обязана слушаться темпа и подсвечиваться,
            // как ключ и BPM.
            //
            // Сергей 18.08 (скрин: песня в D +2, BPM 148, в шапке 12:43, а волна
            // и секции кончаются около 11:50): «нужно бы так же самое жёлтым
            // подсвечивать, как и другие секции наши, именно время — то, что у
            // нас сократилось от оригинала». `D +2` и `BPM 148` уже янтарные,
            // время — нет, хотя при `playbackTempo ≠ 1` звучит оно другое.
            //
            // Механику не заводим вторую: `rate`/`active`/`selected` считаются здесь
            // ровно теми же тремя строками, что у чипа BPM выше.
            const rate = this._rate();
            const active = rate !== 1;
            // rate > 1 — играем быстрее, значит звучит КОРОЧЕ (у BPM тот же
            // множитель работает в другую сторону: bpm × rate).
            const effDur = (dur || 0) / rate;
            // ⚠️ `data-sec` остаётся СЫРЫМ намеренно: двойным кликом правят
            // длительность ПЕСНИ, а не растянутое звучание. Подставь сюда
            // effDur — и редактор молча записал бы растяжку в саму песню.
            return html `<bw-button kind="default" size="md"
                data-chip="dur"
                tone="default"
                ?selected=${active}
                ?data-active=${active}
                data-sec=${Math.floor(dur || 0)}
                @dblclick=${this._onDurationDblClick}
              >${formatMmSs(effDur)}</bw-button>`;
        })()
            : nothing}
        </div>
        ${song?.notes
            ? html `<div
              class="bw-sh-notes"
              title=${this.notesTooltip}
              @dblclick=${this._onNotesDblClick}
            >${song.notes}</div>`
            : nothing}
      </div>
      <div class="bw-sh-right">
        <slot name="actions"></slot>
      </div>
    `;
    }
    _swapToInput(chip, currentSec) {
        const inp = document.createElement('input');
        inp.type = 'text';
        inp.className = 'bw-sh-dur-input';
        inp.placeholder = 'mm:ss';
        inp.value = formatMmSs(currentSec);
        // Hide chip + insert input as sibling (don't detach chip via replaceWith).
        // Detaching causes Lit to re-render bw-tag's template on re-attach,
        // wiping the projected slot content (see B-054 — light-DOM slot
        // projection only runs once in firstUpdated; subsequent re-renders
        // overwrite the inner <span> with an empty <slot> placeholder).
        // Same hide-don't-detach guard as before: chip is now a <bw-button>, whose
        // light-DOM slot projection (.bw-button-inner span) also only runs once in
        // firstUpdated — detaching/re-attaching would wipe the projected text
        // (same class of bug as B-054 on bw-tag). Hiding via display keeps it intact.
        chip.style.display = 'none';
        chip.parentNode.insertBefore(inp, chip);
        inp.focus();
        inp.select();
        let done = false;
        const cleanup = () => {
            inp.remove();
            chip.style.removeProperty('display');
        };
        const commit = () => {
            if (done)
                return;
            done = true;
            const sec = parseMmSs(inp.value);
            cleanup();
            if (sec != null) {
                chip.setAttribute('data-sec', String(sec));
                // Update the projected slot text only. dur is a <bw-button>, whose
                // default-slot content lands in .bw-button-label (sibling of the
                // icon spans inside .bw-button-inner) — target that so we don't wipe
                // the button chrome. Fall back to .bw-tag-inner for a legacy bw-tag
                // chip, then bare textContent as a last resort.
                const inner = chip.querySelector('.bw-button-label')
                    ?? chip.querySelector('.bw-tag-inner');
                // Через `_rate()`, а не напрямую: `data-sec` держит СЫРУЮ длительность
                // песни, а ярлык показывает её звучание. Разошлись они ровно здесь.
                const shown = formatMmSs(sec / this._rate());
                if (inner)
                    inner.textContent = shown;
                else
                    chip.textContent = shown;
                this.dispatchEvent(new CustomEvent('bw-song-header-duration-change', {
                    detail: { seconds: sec },
                    bubbles: true, composed: true,
                }));
            }
        };
        const cancel = () => {
            if (done)
                return;
            done = true;
            cleanup();
        };
        inp.addEventListener('keydown', (ev) => {
            if (ev.key === 'Enter')
                commit();
            if (ev.key === 'Escape')
                cancel();
        });
        inp.addEventListener('blur', commit);
    }
};
__decorate([
    property({ attribute: false })
], BwSongHeader.prototype, "song", void 0);
__decorate([
    property({ type: Number, attribute: 'audio-duration' })
], BwSongHeader.prototype, "audioDuration", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BwSongHeader.prototype, "compact", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'editable-duration' })
], BwSongHeader.prototype, "editableDuration", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'editable-notes' })
], BwSongHeader.prototype, "editableNotes", void 0);
__decorate([
    property({ type: String, attribute: 'no-song-label' })
], BwSongHeader.prototype, "noSongLabel", void 0);
__decorate([
    property({ type: String, attribute: 'notes-tooltip' })
], BwSongHeader.prototype, "notesTooltip", void 0);
BwSongHeader = __decorate([
    customElement('bw-song-header')
], BwSongHeader);
export { BwSongHeader };
//# sourceMappingURL=bw-song-header.js.map